import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory, FlaskConical } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';

// Main Menu landing page — tile-based launcher
export const MainMenu = ({ onNavigate }) => {
  const { canNav, user } = useAuth();
  const tiles = [
    { id: 'new-order', label: 'New Order', icon: Plus, color: '#C05C35', desc: 'Create an order for a dealer' },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, color: '#2B5339', desc: 'View & manage all orders' },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: '#5C6B61', desc: 'Analytics overview' },
    { id: 'product-summary', label: 'Product Summary', icon: ClipboardList, color: '#2B5339', desc: 'Product-wise order summary' },
    { id: 'products', label: 'Products', icon: Package, color: '#2B5339', desc: 'Catalogue & pricing' },
    { id: 'dealers', label: 'Dealers', icon: Users, color: '#C05C35', desc: 'Dealer masterlist' },
    { id: 'dealer-crm', label: 'Dealer CRM', icon: IndianRupee, color: '#2B5339', desc: 'Outstandings, overdue, dispatch history' },
    { id: 'reports', label: 'Reports', icon: BarChart3, color: '#5C6B61', desc: 'Dealer-wise report' },
    { id: 'transport', label: 'Transport', icon: Truck, color: '#2B5339', desc: 'Transporters, vehicles, drivers' },
    { id: 'stock', label: 'Stock', icon: Warehouse, color: '#2B5339', desc: 'Facility-wise finished-goods stock' },
    { id: 'planning', label: 'Planning', icon: ClipboardList, color: '#2B5339', desc: 'Orders demand vs stock → suggested production' },
    { id: 'production', label: 'Production', icon: Factory, color: '#C05C35', desc: 'Daily production log — E-302 & E-303' },
    { id: 'users', label: 'Users', icon: UserRound, color: '#2B5339', desc: 'Add staff & assign roles' },
    { id: 'costing', label: 'Product Costing', icon: FlaskConical, color: '#C05C35', desc: 'Raw material rates, formulations & cost sheets', adminOnly: true },
  ].filter((t) => (t.adminOnly ? user?.role === 'admin' : canNav(t.id)));
  return (
    <div data-testid="admin-main-menu">
      <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: '#2B5339' }}>Main Menu</h2>
      <p className="mb-6 md:mb-8 text-sm" style={{ color: '#5C6B61' }}>Choose a section to get started.</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 md:gap-3">
        {tiles.map((t) => (
          <button
            key={t.id}
            onClick={() => onNavigate(t.id)}
            className="p-2.5 md:p-3 rounded-lg border bg-white text-left transition-all hover:shadow-md hover:-translate-y-0.5"
            style={{ borderColor: '#E2DFD8' }}
            data-testid={`menu-tile-${t.id}`}
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-md flex items-center justify-center mb-1.5" style={{ background: t.color }}>
              <t.icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
            <p className="text-xs md:text-sm font-semibold leading-tight" style={{ color: '#1E2420' }}>{t.label}</p>
            <p className="text-[10px] md:text-xs mt-0.5 leading-tight line-clamp-2" style={{ color: '#5C6B61' }}>{t.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

